import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelPurchasePayment,
  closePurchaseRequest,
  comparePurchaseQuotations,
  createPurchasePayment,
  createPurchaseQuotation,
  createPurchaseRequest,
  createQuotationException,
  decidePurchaseRequest,
  deletePurchaseAttachment,
  getPurchaseDashboard,
  getPurchaseRequest,
  listApprovalQueue,
  listClients,
  listCostCenters,
  listEligibleSuppliers,
  listProjects,
  listPurchaseApprovals,
  listPurchasePayments,
  listPurchaseQuotations,
  listPurchaseRequests,
  listPurchaseTimeline,
  markPurchasePaymentPaid,
  reconcilePurchasePayment,
  schedulePurchasePayment,
  selectPurchaseQuotation,
  submitPurchaseQuotation,
  submitPurchaseRequest,
  uploadPaymentProof,
  uploadPurchaseAttachment,
  uploadQuotationProposal,
  updatePurchaseRequest,
  type CreatePurchaseRequestPayload,
  type CreateQuotationPayload,
  type PurchaseListParams,
} from '../api';

export const PURCHASE_KEYS = {
  all: ['compras'] as const,
  requests: (params?: PurchaseListParams) => ['compras', 'solicitacoes', params] as const,
  request: (id: string) => ['compras', 'solicitacao', id] as const,
  quotations: (id: string) => ['compras', 'solicitacao', id, 'cotacoes'] as const,
  comparison: (id: string) => ['compras', 'solicitacao', id, 'equalizacao'] as const,
  approvals: (id: string) => ['compras', 'solicitacao', id, 'aprovacoes'] as const,
  timeline: (id: string) => ['compras', 'solicitacao', id, 'timeline'] as const,
  payments: (id: string) => ['compras', 'solicitacao', id, 'pagamentos'] as const,
  approvalQueue: ['compras', 'aprovacoes', 'fila'] as const,
  dashboard: ['compras', 'relatorios', 'dashboard'] as const,
  masterData: {
    costCenters: ['compras', 'catalogos', 'centros-custo'] as const,
    clients: ['compras', 'catalogos', 'clientes'] as const,
    projects: ['compras', 'catalogos', 'projetos'] as const,
    suppliers: ['compras', 'catalogos', 'fornecedores'] as const,
  },
};

function usePurchaseMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_KEYS.all }),
  });
}

export function usePurchaseRequests(params?: PurchaseListParams) {
  return useQuery({ queryKey: PURCHASE_KEYS.requests(params), queryFn: () => listPurchaseRequests(params) });
}

export function usePurchaseRequest(id: string | undefined) {
  return useQuery({
    queryKey: PURCHASE_KEYS.request(id ?? 'noop'),
    queryFn: () => getPurchaseRequest(id as string),
    enabled: Boolean(id),
  });
}

export function useCreatePurchaseRequest() {
  return usePurchaseMutation<CreatePurchaseRequestPayload, Awaited<ReturnType<typeof createPurchaseRequest>>>(
    createPurchaseRequest,
  );
}

export function useUpdatePurchaseRequest() {
  return usePurchaseMutation<
    { id: string; payload: CreatePurchaseRequestPayload },
    Awaited<ReturnType<typeof updatePurchaseRequest>>
  >(({ id, payload }) => updatePurchaseRequest(id, payload));
}

export function useUploadPurchaseAttachment() {
  return usePurchaseMutation<
    { requestId: string; file: File; kind?: string; description?: string },
    Awaited<ReturnType<typeof uploadPurchaseAttachment>>
  >(({ requestId, ...input }) => uploadPurchaseAttachment(requestId, input));
}

export function useDeletePurchaseAttachment() {
  return usePurchaseMutation<{ requestId: string; attachmentId: string }, void>(
    ({ requestId, attachmentId }) => deletePurchaseAttachment(requestId, attachmentId),
  );
}

export function useSubmitPurchaseRequest() {
  return usePurchaseMutation<string, Awaited<ReturnType<typeof submitPurchaseRequest>>>(submitPurchaseRequest);
}

export function useDecisionPurchaseRequest() {
  return usePurchaseMutation<
    { id: string; decision: 'approve' | 'reject' | 'return'; stepKey?: string; justification?: string },
    Awaited<ReturnType<typeof decidePurchaseRequest>>
  >(({ id, ...payload }) => decidePurchaseRequest(id, payload));
}

export function useSelectPurchaseQuotation() {
  return usePurchaseMutation<
    { id: string; quotationId: string; justification?: string },
    Awaited<ReturnType<typeof selectPurchaseQuotation>>
  >(({ id, ...payload }) => selectPurchaseQuotation(id, payload));
}

export function useClosePurchaseRequest() {
  return usePurchaseMutation<string, Awaited<ReturnType<typeof closePurchaseRequest>>>(closePurchaseRequest);
}

export function usePurchaseQuotations(requestId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: PURCHASE_KEYS.quotations(requestId ?? 'noop'),
    queryFn: () => listPurchaseQuotations(requestId as string),
    enabled: Boolean(requestId) && enabled,
  });
}

export function useCreatePurchaseQuotation() {
  return usePurchaseMutation<CreateQuotationPayload, Awaited<ReturnType<typeof createPurchaseQuotation>>>(
    createPurchaseQuotation,
  );
}

export function useUploadQuotationProposal() {
  return usePurchaseMutation<
    { id: string; file: File },
    Awaited<ReturnType<typeof uploadQuotationProposal>>
  >(({ id, file }) => uploadQuotationProposal(id, file));
}

export function useSubmitPurchaseQuotation() {
  return usePurchaseMutation<string, Awaited<ReturnType<typeof submitPurchaseQuotation>>>(submitPurchaseQuotation);
}

export function useCreateQuotationException() {
  return usePurchaseMutation<
    { requestId: string; reason: string; justification: string },
    Awaited<ReturnType<typeof createQuotationException>>
  >(({ requestId, ...payload }) => createQuotationException(requestId, payload));
}

export function useEqualizationComparison(requestId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: PURCHASE_KEYS.comparison(requestId ?? 'noop'),
    queryFn: () => comparePurchaseQuotations(requestId as string),
    enabled: Boolean(requestId) && enabled,
    retry: false,
  });
}

export function usePurchaseApprovals(requestId: string | undefined) {
  return useQuery({
    queryKey: PURCHASE_KEYS.approvals(requestId ?? 'noop'),
    queryFn: () => listPurchaseApprovals(requestId as string),
    enabled: Boolean(requestId),
  });
}

export function usePurchaseTimeline(requestId: string | undefined) {
  return useQuery({
    queryKey: PURCHASE_KEYS.timeline(requestId ?? 'noop'),
    queryFn: () => listPurchaseTimeline(requestId as string),
    enabled: Boolean(requestId),
  });
}

export function useApprovalQueue() {
  return useQuery({ queryKey: PURCHASE_KEYS.approvalQueue, queryFn: listApprovalQueue });
}

export function usePurchasePayments(requestId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: PURCHASE_KEYS.payments(requestId ?? 'noop'),
    queryFn: () => listPurchasePayments(requestId as string),
    enabled: Boolean(requestId) && enabled,
  });
}

export function useCreatePurchasePayment() {
  return usePurchaseMutation<Parameters<typeof createPurchasePayment>[0], Awaited<ReturnType<typeof createPurchasePayment>>>(
    createPurchasePayment,
  );
}

export function useSchedulePurchasePayment() {
  return usePurchaseMutation<
    { id: string; scheduledFor: string },
    Awaited<ReturnType<typeof schedulePurchasePayment>>
  >(({ id, scheduledFor }) => schedulePurchasePayment(id, scheduledFor));
}

export function useMarkPurchasePaymentPaid() {
  return usePurchaseMutation<
    { id: string; paymentReference: string; paidAt?: string },
    Awaited<ReturnType<typeof markPurchasePaymentPaid>>
  >(({ id, ...payload }) => markPurchasePaymentPaid(id, payload));
}

export function useReconcilePurchasePayment() {
  return usePurchaseMutation<
    { id: string; reconciliationReference: string; reconciledAt?: string },
    Awaited<ReturnType<typeof reconcilePurchasePayment>>
  >(({ id, ...payload }) => reconcilePurchasePayment(id, payload));
}

export function useCancelPurchasePayment() {
  return usePurchaseMutation<
    { id: string; justification: string },
    Awaited<ReturnType<typeof cancelPurchasePayment>>
  >(({ id, justification }) => cancelPurchasePayment(id, justification));
}

export function useUploadPaymentProof() {
  return usePurchaseMutation<
    { id: string; file: File },
    Awaited<ReturnType<typeof uploadPaymentProof>>
  >(({ id, file }) => uploadPaymentProof(id, file));
}

export function usePurchaseDashboard(enabled = true) {
  return useQuery({ queryKey: PURCHASE_KEYS.dashboard, queryFn: getPurchaseDashboard, enabled });
}

export function usePurchaseCatalogs(includeSuppliers = true) {
  const costCenters = useQuery({ queryKey: PURCHASE_KEYS.masterData.costCenters, queryFn: listCostCenters, staleTime: 300_000 });
  const clients = useQuery({ queryKey: PURCHASE_KEYS.masterData.clients, queryFn: listClients, staleTime: 300_000 });
  const projects = useQuery({ queryKey: PURCHASE_KEYS.masterData.projects, queryFn: listProjects, staleTime: 300_000 });
  const suppliers = useQuery({ queryKey: PURCHASE_KEYS.masterData.suppliers, queryFn: listEligibleSuppliers, staleTime: 60_000, enabled: includeSuppliers });
  return { costCenters, clients, projects, suppliers };
}
